import React, { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import quotePageStyle from "features/quote/styles";
import { emptyString } from "constants/defaultValues";
import { Popover, Grid, Button, makeStyles, Paper, useMediaQuery, useTheme, Tooltip } from "@material-ui/core";
import { Typography } from "@totalsoft/rocket-webapp-components-deprecated";
import { QUOTE_HEADER_QUERY } from "features/quote/queries";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import PropTypes from "prop-types";
import {
  RecentActors,
  ListAlt,
  Assignment,
  KeyboardArrowDown,
  RadioButtonUnchecked,
  Lens,
  Explore,
} from "@material-ui/icons";
import { quoteSteps, zootDocDecisionColor } from "../constants";
import { GET_CLIENT_NAME_QUERY } from "features/client/queries";
import { useLocation } from "react-router-dom";
import cx from "classnames";
import ActionsDialog from "features/common/components/ActionsDialog";
import QuoteDecisionContent from "./QuoteDecisionComponent";
import { invalidateApolloClientCache } from "utils/functions";
import { useApolloClient } from "@apollo/client";
import useUserData from "hooks/useUserData";
import { getTenant } from "providers/TenantProvider";
import { countryCodeConstants } from "features/common/constants";
import QuoteHeaderPartners from "./QuoteHeaderPartners";

const useStyles = makeStyles(quotePageStyle);

function QuoteHeaderSummary({ quoteId, activeStepId }) {
  const location = useLocation();
  const { t } = useTranslation();
  const client = useApolloClient();
  const { allowAutomatedDecision } = useUserData();
  const { countryCode } = getTenant();

  const quoteExists = Boolean(quoteId);

  const [anchorEl_PartName, setAnchorEl_PartName] = useState(null);
  const open_PartName = Boolean(anchorEl_PartName);
  const [anchorEl_Assets, setAnchorEl_Assets] = useState(null);
  const open_Assets = Boolean(anchorEl_Assets);

  const [anchorEl_SelectedProduct, setAnchorEl_SelectedProduct] = useState(null);
  const open_SelectedProduct = Boolean(anchorEl_SelectedProduct);
  const [decisionAnchorEl, setDecisionAnchorEl] = useState(null);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("sm"));

  const { data } = useQueryWithErrorHandling(QUOTE_HEADER_QUERY, {
    variables: { quoteId, loadProducts: activeStepId < quoteSteps.financeProduct },
    skip: !quoteExists,
  });
  const quote = data?.quote;
  const quoteIsEstimation = quote?.isEstimation || location.pathname === "/estimations/new/assets";
  const selectedProduct = data?.quote?.quoteProducts?.find((x) => x.selected === true);

  const { data: clientName } = useQueryWithErrorHandling(GET_CLIENT_NAME_QUERY, {
    variables: { clientId: location.state?.clientId },
    skip: quoteExists || !location.state?.clientId || quoteIsEstimation,
  });
  const addedClientName = clientName?.getClientName || emptyString;

  const styleProps = useMemo(() => ({ colorDecision: quote?.zootDocDecisionColor }), [quote?.zootDocDecisionColor]);
  const classes = useStyles(styleProps);

  const handlePartnerNameClick = useCallback((event) => quoteExists && setAnchorEl_PartName(event.currentTarget), [
    quoteExists,
  ]);
  const handleAssetsClick = useCallback((event) => setAnchorEl_Assets(event.currentTarget), []);
  const handleSelectedProductClick = useCallback((event) => setAnchorEl_SelectedProduct(event.currentTarget), []);
  const handleViewDecison = useCallback((event) => setDecisionAnchorEl(event.currentTarget), []);

  const handleClose = useCallback(() => {
    setAnchorEl_PartName(null);
    setAnchorEl_Assets(null);
    setAnchorEl_SelectedProduct(null);
    setDecisionAnchorEl(null);
  }, []);

  const headerCaption = useMemo(() => {
    if (quoteIsEstimation) return quote?.estimationName || emptyString;
    return quote?.quoteHeaderPartnerName || addedClientName;
  }, [quote, addedClientName, quoteIsEstimation]);

  return (
    <Paper elevation={2} className={classes.paperQuoteHeader}>
      <Grid container className={classes.wizardInfo} direction="row">
        {activeStepId > quoteSteps.client && (
          <Grid item xs={2} className={classes.height100}>
            <Button
              fullWidth
              aria-describedby={open_PartName ? "idPartnerName" : undefined}
              variant="contained"
              className={cx(classes.wizardCompanyName, classes.btnWizard)}
              onClick={handlePartnerNameClick}
              startIcon={quoteIsEstimation ? <Explore /> : <RecentActors />}
              endIcon={quoteExists && <KeyboardArrowDown />}
            >
              {!matches && (
                <Typography noWrap className={classes.headerCaption}>
                  {headerCaption}
                </Typography>
              )}
            </Button>
            {open_PartName && (
              <Popover
                id={open_PartName ? "idPartnerName" : undefined}
                open={open_PartName}
                anchorEl={anchorEl_PartName}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <QuoteHeaderPartners quoteId={quoteId} clientName={headerCaption} />
              </Popover>
            )}
          </Grid>
        )}
        {quote && activeStepId > quoteSteps.assets && (
          <Grid item xs={4} className={classes.height100}>
            <Button
              aria-describedby={open_Assets ? "idAssets" : undefined}
              variant="contained"
              onClick={handleAssetsClick}
              startIcon={<ListAlt />}
              endIcon={<KeyboardArrowDown />}
              className={classes.btnWizard}
            >
              {!matches && (
                <Grid item container direction="row" alignItems="center" className={classes.fontSize12}>
                  <Grid item xs={6} container direction="column" alignItems="center">
                    <Grid item xs className={classes.labelWizard}>
                      <Typography>{t("Quote.Assets")}</Typography>
                    </Grid>
                    <Grid item xs className={classes.gridItemWizard}>
                      <Typography>
                        {quote.assetNo + " " + (quote.assetNo > 1 ? t("Asset.Items") : t("Asset.Item"))}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item container direction="column" xs={6}>
                    <Grid item xs className={classes.labelWizard}>
                      <Typography>
                        {quote.showAmountsWithVAT ? t("Quote.TotalNetWithVAT") : t("Quote.TotalNet")}
                      </Typography>
                    </Grid>
                    <Grid item xs className={classes.gridItemWizard}>
                      <Typography>
                        {t("DECIMAL2", {
                          number: (quote.showAmountsWithVAT ? quote.totalAmountWithVAT : quote.totalAmount) || 0,
                        }) +
                          " " +
                          quote.currencyCode}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Button>
            {open_Assets && (
              <Popover
                id={open_Assets ? "idAssets" : undefined}
                open={open_Assets}
                anchorEl={anchorEl_Assets}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                <Paper variant="outlined" className={cx(classes.paperDialog, classes.width75)}>
                  <Grid container direction="row" alignItems="center">
                    {quote.quoteAssets &&
                      quote.quoteAssets.map((asset, idx) => (
                        <Grid item container key={idx} direction="row">
                          <Grid item xs={4}>
                            <Typography variant="overline" className={classes.grayColor}>
                              {asset.brandName}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="overline" className={classes.grayColor}>
                              {t("Asset.Asset")}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="overline" className={classes.grayColor}>
                              {quote.showAmountsWithVAT ? t("Asset.AssetNetPriceWithVAT") : t("Asset.AssetNetPrice")}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography> {asset.kindOfObjectName}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography>
                              {asset.qtty} x {asset.assetName}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography>
                              {t("DECIMAL2", {
                                number:
                                  (quote.showAmountsWithVAT ? asset.assetNetPriceWithVAT : asset.assetNetPrice) || 0,
                              }) +
                                " " +
                                quote.currencyCode}
                            </Typography>
                          </Grid>
                        </Grid>
                      ))}
                  </Grid>
                </Paper>
              </Popover>
            )}
          </Grid>
        )}
        {selectedProduct && activeStepId > quoteSteps.financeProduct && (
          <>
            <Grid item xs={4} className={classes.height100}>
              <Tooltip title={selectedProduct.productName || emptyString}>
                <Button
                  aria-describedby={open_SelectedProduct ? "idProduct" : undefined}
                  variant="contained"
                  onClick={handleSelectedProductClick}
                  startIcon={<Assignment />}
                  endIcon={<KeyboardArrowDown />}
                  className={classes.btnWizard}
                >
                  {!matches && (
                    <Grid
                      container
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      className={classes.fontSize12}
                    >
                      <Grid item xs={8} container direction="column" alignItems="center">
                        <Grid item xs className={classes.labelWizard}>
                          <Typography>{t("Product.ProductType")}</Typography>
                        </Grid>
                        <Grid item xs className={classes.gridItemWizard}>
                          <Typography>
                            {selectedProduct.productName?.length > 15
                              ? selectedProduct.productName.substr(0, 15) + "..."
                              : selectedProduct.productName}
                          </Typography>
                        </Grid>
                      </Grid>
                      {selectedProduct.scheduleIsConfigurable &&
                        countryCode !== countryCodeConstants.china &&
                        countryCode !== countryCodeConstants.romania && (
                          <Grid item xs={4} className={classes.grayColor}>
                            <Typography>{t("Quote.SeasonalInstallments")}</Typography>
                          </Grid>
                        )}
                      {!selectedProduct.scheduleIsConfigurable && countryCode === countryCodeConstants.brazil && (
                        <Typography variant="body2" className={classes.scheduleType}>
                          {t("Product.InstallmentAmount")}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Button>
              </Tooltip>
              {open_SelectedProduct && (
                <Popover
                  id={open_SelectedProduct ? "idSelectedProduct" : undefined}
                  open={open_SelectedProduct}
                  anchorEl={anchorEl_SelectedProduct}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <Paper variant="outlined" className={cx(classes.paperDialog, classes.width45)}>
                    <Grid container direction="row" alignItems="center">
                      {selectedProduct && (
                        <Grid item container direction="row" alignItems="center">
                          <Grid item xs={6}>
                            <Typography variant="overline" className={classes.grayColor}>
                              {quote.showAmountsWithVAT ? t("Quote.DownPaymentWithVAT") : t("Quote.DownPayment")}{" "}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="overline" className={classes.grayColor}>
                              {t("Quote.Term")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography>
                              {`${t("DECIMAL2", {
                                number: quote.showAmountsWithVAT
                                  ? selectedProduct.downPaymentAmountWithVAT
                                  : selectedProduct.downPaymentAmount,
                              })}` +
                                " " +
                                quote.currencyCode}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography>
                              {selectedProduct.duration} {t("EditProduct.Months")}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Popover>
              )}
            </Grid>
            {allowAutomatedDecision && quote.zootDocDecisionFinished != null && (
              <>
                <Grid item xs={1}>
                  <Button onClick={handleViewDecison}>
                    {quote.zootDocDecisionColor ? (
                      <Lens className={classes.zootDocDecision} />
                    ) : (
                      <RadioButtonUnchecked />
                    )}
                  </Button>
                </Grid>
                <ActionsDialog
                  fullWidth={true}
                  open={Boolean(decisionAnchorEl)}
                  handleClose={handleClose}
                  title={t("Quote.Decision")}
                  initialState={{ quoteId: quote.quoteId }}
                  Content={QuoteDecisionContent}
                  actions={[
                    {
                      caption: t("General.Refresh"),
                      disabled:
                        quote.zootDocDecisionFinished &&
                        (quote.zootDocDecisionColor === zootDocDecisionColor.green ||
                          quote.zootDocDecisionColor === zootDocDecisionColor.red),
                      handle: () => {
                        invalidateApolloClientCache(client, { __typename: "Quote", quoteId: quote.quoteId }, [
                          "zootDocDecisionFinished",
                          "zootDocDecisionColor",
                          "zootDocDecisionReasons",
                        ]);
                        return true;
                      },
                    },
                    {
                      caption: t("General.Close"),
                      handle: () => {
                        return true;
                      },
                    },
                  ]}
                />
              </>
            )}
          </>
        )}
      </Grid>
    </Paper>
  );
}

QuoteHeaderSummary.propTypes = {
  quoteId: PropTypes.number,
  activeStepId: PropTypes.number.isRequired,
};

export default QuoteHeaderSummary;
